import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  Mail,
  MessageSquare,
  BrainCircuit,
  Calendar,
  Bot,
  Trello,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { slackService } from "../../services/SlackService";
import { asanaService } from "../../services/AsanaService";
import { useToastContext } from "../../context/ToastContext";
import { useLoader } from "../../context/LoaderContext";

const AsanaLandingPage = () => {
  const navigate = useNavigate();
  const { showToast } = useToastContext();
  const { showLoader, hideLoader } = useLoader();
  const [connections, setConnections] = useState({
    slack: { connected: false, loading: true },
    asana: { connected: false, loading: true },
  });

  useEffect(() => {
    const checkConnectionStatuses = async () => {
      try {
        const [slackStatus, asanaStatus] = await Promise.all([
          slackService.getSlackStatus(),
          asanaService.getAsanaStatus(),
        ]);

        setConnections({
          slack: { connected: slackStatus.data.connected, loading: false },
          asana: { connected: asanaStatus.data.connected, loading: false },
        });
      } catch (error: any) {
        showToast("error", "Failed to fetch connection statuses.");
        setConnections({
          slack: { connected: false, loading: false },
          asana: { connected: false, loading: false },
        });
      }
    };

    checkConnectionStatuses();
  }, []);

  const handleFetchSlackUsers = async () => {
    showLoader();
    try {
      const response = await slackService.fetchSlackUsers();
      console.log("Slack users:", response.data.users);
      showToast("success", "Slack users fetched successfully");
    } catch (error: any) {
      console.error("Error fetching Slack users:", error);
      showToast("error", error.message || "Failed to fetch Slack users");
    } finally {
      hideLoader();
    }
  };

  const handleFetchAsanaProjects = async () => {
    showLoader();
    try {
      const response = await asanaService.getAsanaProjects();
      console.log("Asana projects:", response.data.projects);
      showToast("success", "Asana projects fetched successfully");
    } catch (error: any) {
      console.error("Error fetching Asana projects:", error);
      showToast("error", error.message || "Failed to fetch Asana projects");
    } finally {
      hideLoader();
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-background-default dark:bg-background-dark">
      <main className="flex-1">
        <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -inset-[10px] bg-gradient-to-r from-primary/5 via-secondary/5 to-primary/5 dark:from-primary-dark/10 dark:via-secondary/10 dark:to-primary-dark/10 blur-3xl" />
          </div>

          <div className="relative max-w-6xl mx-auto">
            <div className="text-center space-y-8">
              <div className="inline-flex items-center justify-center p-2 rounded-2xl bg-background-paper/50 dark:bg-background-paperDark/50 backdrop-blur-sm border border-divider dark:border-divider-dark mb-6">
                <div className="flex items-center gap-3 px-4 py-2">
                  <BrainCircuit className="w-5 h-5 text-primary dark:text-primary-dark" />
                  <span className="text-text-primary dark:text-text-primaryDark font-medium">
                    AI-Powered Automation
                  </span>
                </div>
              </div>

              <div className="space-y-1">
                <h1 className="text-4xl md:text-5xl lg:text-7xl font-black tracking-tight">
                  <div className="text-primary dark:text-primary-dark">
                    Project Delivery
                  </div>
                  <div className="text-text-primary dark:text-text-primaryDark">
                    Made Simple
                  </div>
                </h1>
              </div>

              <p className="mt-6 text-xl text-text-secondary dark:text-text-secondaryDark max-w-3xl mx-auto leading-relaxed">
                Streamline your end-of-week deliverables and project management
                with AI-powered automation.
              </p>

              {/* Integration Status Cards */}
              <div className="flex justify-center gap-6 mt-12">
                <div className="flex flex-col items-center p-6 rounded-xl bg-background-paper/50 dark:bg-background-paperDark/50 backdrop-blur-sm border border-divider dark:border-divider-dark">
                  <div className="flex items-center gap-3">
                    <Trello className="w-6 h-6 text-[#F06A6A]" />
                    <span className="font-semibold">Asana</span>
                    {connections.asana.loading ? (
                      <div className="w-5 h-5 animate-pulse bg-secondary/20 rounded-full" />
                    ) : connections.asana.connected ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500" />
                    )}
                  </div>
                  <p className="mt-2 text-sm text-text-secondary dark:text-text-secondaryDark">
                    Project Management
                  </p>
                </div>

                <div className="flex flex-col items-center p-6 rounded-xl bg-background-paper/50 dark:bg-background-paperDark/50 backdrop-blur-sm border border-divider dark:border-divider-dark">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="w-6 h-6 text-[#36C5F0]" />
                    <span className="font-semibold">Slack</span>
                    {connections.slack.loading ? (
                      <div className="w-5 h-5 animate-pulse bg-secondary/20 rounded-full" />
                    ) : connections.slack.connected ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500" />
                    )}
                  </div>
                  <p className="mt-2 text-sm text-text-secondary dark:text-text-secondaryDark">
                    Team Communication
                  </p>
                </div>
              </div>

              <div className="flex justify-center gap-4 mt-8">
                {connections.slack.connected && (
                  <button
                    onClick={handleFetchSlackUsers}
                    className="px-6 py-3 bg-background-paper dark:bg-background-paperDark border border-divider dark:border-divider-dark text-text-primary dark:text-text-primaryDark font-medium rounded-lg hover:border-[#36C5F0]/50 dark:hover:border-[#36C5F0]/50 transition-all duration-300"
                  >
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-[#36C5F0]" />
                      Fetch Slack Users
                    </div>
                  </button>
                )}

                {connections.asana.connected && (
                  <button
                    onClick={handleFetchAsanaProjects}
                    className="px-6 py-3 bg-background-paper dark:bg-background-paperDark border border-divider dark:border-divider-dark text-text-primary dark:text-text-primaryDark font-medium rounded-lg hover:border-[#F06A6A]/50 dark:hover:border-[#F06A6A]/50 transition-all duration-300"
                  >
                    <div className="flex items-center gap-2">
                      <Trello className="w-5 h-5 text-[#F06A6A]" />
                      Fetch Asana Projects
                    </div>
                  </button>
                )}
              </div>

              <div className="flex justify-center mt-8">
                <Link
                  to="/integrations"
                  className="group inline-flex items-center gap-2 px-6 py-3 bg-text-primary dark:bg-text-primaryDark text-background-default dark:text-background-dark rounded-lg font-medium hover:bg-text-primary/90 dark:hover:bg-text-primaryDark/90 transition-colors"
                >
                  Manage Integrations
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>

              <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  {
                    icon: <Bot className="w-6 h-6" />,
                    title: "AI Assistant",
                    description:
                      "Intelligent automation for project management tasks.",
                  },
                  {
                    icon: <ClipboardList className="w-6 h-6" />,
                    title: "Task Management",
                    description:
                      "Streamlined workflow for managing project deliverables.",
                  },
                  {
                    icon: <Calendar className="w-6 h-6" />,
                    title: "Weekly Reports",
                    description:
                      "Automated end-of-week reporting and status updates.",
                  },
                ].map((feature, index) => (
                  <div
                    key={index}
                    className="flex flex-col items-center p-6 rounded-xl bg-background-paper/50 dark:bg-background-paperDark/50 backdrop-blur-sm border border-divider dark:border-divider-dark"
                  >
                    <div className="w-12 h-12 rounded-lg bg-primary/10 dark:bg-primary-dark/10 flex items-center justify-center mb-4">
                      <div className="text-primary dark:text-primary-dark">
                        {feature.icon}
                      </div>
                    </div>
                    <h3 className="text-lg font-semibold text-text-primary dark:text-text-primaryDark mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-text-secondary dark:text-text-secondaryDark text-center">
                      {feature.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="py-8 px-4 sm:px-6 lg:px-8 bg-background-paper dark:bg-background-paperDark border-t border-divider dark:border-divider-dark">
        <div className="max-w-6xl mx-auto text-center text-sm text-text-secondary dark:text-text-secondaryDark">
          <p>© {new Date().getFullYear()} Forgeworks. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default AsanaLandingPage;
